import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import logo from "../media3/header/skinforge.png";
import userIcon from "../media3/header/user.svg";
import profile from "../media3/header/profile.jpg";

function Header({ isLoggedIn, setLoginModalOpen, handleLogout }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [userData, setUserData] = useState({ username: "", email: "" });

  const desktopMenuRef = useRef(null);
  const navigate = useNavigate();

  // === Update user data when logged in ===
  useEffect(() => {
    if (isLoggedIn) {
      const email = localStorage.getItem("userEmail") || localStorage.getItem("email");
      setUserData({
        username: email ? email.split("@")[0] : "User",
        email: email || "No email found",
      });
    } else {
      setUserData({ username: "", email: "" });
      setShowUserMenu(false);
    }
  }, [isLoggedIn]);

  // === Close dropdown when clicking outside ===
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (desktopMenuRef.current && !desktopMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // === Prevent body scroll when mobile menu is open ===
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // === Handlers ===
  const handleLoginClick = () => {
    setLoginModalOpen(true);
    setIsMobileMenuOpen(false);
  };

  const toggleUserMenu = () => {
    if (isLoggedIn) {
      setShowUserMenu((prev) => !prev);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleMobileLogout = () => {
    handleLogout();
    setIsMobileMenuOpen(false);
  };

  const handleAboutClick = () => {
    closeMobileMenu();
    navigate("/");

    const scrollToSection = () => {
      const section = document.getElementById("about-us-section");
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      } else {
        setTimeout(scrollToSection, 100);
      }
    };

    setTimeout(scrollToSection, 200);
  };

  const handleMobileLinkClick = () => {
    closeMobileMenu();
  };

  return (
    <>
      <header className="navbar">
        {/* Logo */}
        <div className="logo">
          <Link to="/" onClick={closeMobileMenu}>
            <img src={logo} alt="Logo" />
          </Link>
        </div>

        {/* Hamburger */}
        <div className="hamburger" onClick={() => setIsMobileMenuOpen(true)}>
          <span></span>
          <span></span>
          <span></span>
        </div>

        {/* Desktop Nav */}
        <nav className="nav-links">
          <Link to="/">Home</Link>
          <Link to="/categories">Categories</Link>
          <Link to="/" onClick={handleAboutClick}>About Us</Link>
          <Link to="/contact">Contact</Link>
        </nav>

        {/* Desktop Auth */}
        <div className="authButtons-desktopOnly" ref={desktopMenuRef}>
          {!isLoggedIn ? (
            <button onClick={handleLoginClick} className="getStartedBtn">
              Get Started
            </button>
          ) : (
            <div className="user-section">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="user-icon"
                onClick={toggleUserMenu}
                height="32px"
                viewBox="0 -960 960 960"
                width="32px"
                fill="#FFFFFF"
                style={{ cursor: "pointer" }}
              >
                <path d="M234-276q51-39 114-61.5T480-360q69 0 132 22.5T726-276q35-41 54.5-93T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 59 19.5 111t54.5 93Zm246-164q-59 0-99.5-40.5T340-580q0-59 40.5-99.5T480-720q59 0 99.5 40.5T620-580q0 59-40.5 99.5T480-440Zm0 360q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q53 0 100-15.5t86-44.5q-39-29-86-44.5T480-280q-53 0-100 15.5T294-220q39 29 86 44.5T480-160Zm0-360q26 0 43-17t17-43q0-26-17-43t-43-17q-26 0-43 17t-17 43q0 26 17 43t43 17Zm0-60Zm0 360Z" />
              </svg>

              {showUserMenu && (
                <div className="user-dropdown">
                  <button className="close-dropdown" onClick={toggleUserMenu}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      height="22px"
                      viewBox="0 0 24 24"
                      width="22px"
                      fill="#ffffff"
                    >
                      <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z" />
                    </svg>
                  </button>

                  <div className="user-info">
                    <img src={profile} alt="User" className="user-avatar" />
                    <div>
                      <p className="username">{userData.username}</p>
                      <p className="email">{userData.email}</p>
                    </div>
                  </div>
                  <hr />
                  <button onClick={handleLogout} className="logoutBtn">
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${isMobileMenuOpen ? "open" : ""}`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu Sidebar */}
      <div className={`mobile-menu-sidebar ${isMobileMenuOpen ? "open" : ""}`}>
        {/* Close Button */}
        <button className="mobile-menu-close" onClick={closeMobileMenu}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            height="28px"
            viewBox="0 0 24 24"
            width="28px"
            fill="#000.mobile-menu-get-starte"
          >
            <path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11L10.59 12l-4.9 4.89a1 1 0 1 0 1.41 1.42L12 13.41l4.89 4.9a1 1 0 0 0 1.42-1.42L13.41 12l4.9-4.89a1 1 0 0 0-.01-1.4Z" />
          </svg>
        </button>

        {/* User Info (only if logged in) */}
        {isLoggedIn && (
          <div className="mobile-menu-user">
            <img src={profile} alt="User" />
            <div className="mobile-menu-user-info">
              <p className="username">{userData.username}</p>
              <p className="email">{userData.email}</p>
            </div>
          </div>
        )}

        {/* Get Started Button (only if not logged in) */}
        {!isLoggedIn && (
          <button onClick={handleLoginClick} className="mobile-menu-get-started">
            Get Started
          </button>
        )}

        {/* Menu Links */}
        <div className="mobile-menu-links">
          <Link to="/" onClick={handleMobileLinkClick}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
            </svg>
            Home
          </Link>

          <Link to="/categories" onClick={handleMobileLinkClick}>
           <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000"><path d="M360-200v-80h480v80H360Zm0-240v-80h480v80H360Zm0-240v-80h480v80H360ZM200-160q-33 0-56.5-23.5T120-240q0-33 23.5-56.5T200-320q33 0 56.5 23.5T280-240q0 33-23.5 56.5T200-160Zm0-240q-33 0-56.5-23.5T120-480q0-33 23.5-56.5T200-560q33 0 56.5 23.5T280-480q0 33-23.5 56.5T200-400Zm0-240q-33 0-56.5-23.5T120-720q0-33 23.5-56.5T200-800q33 0 56.5 23.5T280-720q0 33-23.5 56.5T200-640Z"/></svg>
            Categories
          </Link>

          <Link to="/" onClick={() => { handleAboutClick(); handleMobileLinkClick(); }}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            About Us
          </Link>

          <Link to="/contact" onClick={handleMobileLinkClick}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
            </svg>
            Contact
          </Link>
        </div>

        {/* Logout Button (only if logged in) */}
        {isLoggedIn && (
          <button onClick={handleMobileLogout} className="mobile-menu-logout">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/>
            </svg>
            Log Out
          </button>
        )}
      </div>
    </>
  );
}

export default Header;